import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ManageHistoryRoundedIcon from "@mui/icons-material/ManageHistoryRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { Button, IconButton, Tab, Tabs, Tooltip } from "@mui/material";
import { actionStatus, actionType, displayLabel, historyDomain, orderStatus, riskLevel, scheduleStatus, sourceType } from "../../constants/labels";
import { OssStatusChip } from "../OssStatusChip";
import { OssChangeList } from "../changes/OssChangeList";
import { getLineActionReason, isActionNeeded } from "../orderDetail/detailUtils";

const Info = ({ label, value }) => <div className="oss-action-info"><dt>{label}</dt><dd>{value ?? "-"}</dd></div>;
const documentTypes = { PURCHASE_ORDER: "발주서", ORDER_FORM: "주문서", CHANGE_ORDER: "변경 근거", SPEC: "규격서", OTHER: "기타" };
const statusDescription = (value, domain) => String(value || "-")
  .split(/\s*(?:->|→)\s*/)
  .map((part) => displayLabel(domain === "ORDER" ? orderStatus : domain === "ACTION" ? actionStatus : scheduleStatus, part))
  .join(" → ");

export const OssOrderDetailWorkspace = ({
  order, summary, tab, onTabChange, lines, data, changes, changesLoading,
  disabledByCancel, submitting, onAction, onDocumentUpload, onDocumentOpen,
  onDocumentDelete, onChangeRequest, onReviewChange,
}) => {
  const documents = data.documents || [];
  const histories = data.histories || [];
  const actions = data.actions || [];
  return (
    <>
      <section className="oss-order-overview" aria-label="주문 정보">
        <dl className="oss-order-info-grid">
          <Info label="고객 발주번호" value={order.customer_po_no} />
          <Info label="주문일자" value={order.order_date} />
          <Info label="담당자" value={order.manager_user_key} />
          <Info label="입력 방식" value={displayLabel(sourceType, order.source_type)} />
          <Info label="최근 수정" value={order.updated_at} />
        </dl>
        {order.note && <p className="oss-order-note"><span>비고</span>{order.note}</p>}
      </section>
      <dl className="oss-order-metrics" aria-label="주문 요약">
        <div><dt>관리 품목</dt><dd>{summary.lineCount}<small>건</small></dd></div>
        <div className={summary.actionCount ? "needs-action" : ""}><dt>조치 필요</dt><dd>{summary.actionCount}<small>건</small></dd></div>
        <div><dt>가장 빠른 착수일</dt><dd>{summary.earliestStart || "-"}</dd></div>
        <div className={Number(summary.minMargin) < 0 ? "needs-action" : ""}><dt>최소 착수 기준 여유일</dt><dd>{summary.minMargin}{summary.minMargin !== "-" && <small>일</small>}</dd></div>
      </dl>
      <Tabs value={tab} onChange={(_, value) => onTabChange(value)} variant="fullWidth" className="oss-order-tabs" aria-label="주문 상세 항목">
        <Tab id="oss-order-tab-0" aria-controls="oss-order-panel-0" icon={<Inventory2OutlinedIcon />} iconPosition="start" label={"품목 / 일정 " + lines.length} />
        <Tab id="oss-order-tab-1" aria-controls="oss-order-panel-1" icon={<DescriptionOutlinedIcon />} iconPosition="start" label={"문서 " + documents.length} />
        <Tab id="oss-order-tab-2" aria-controls="oss-order-panel-2" icon={<ManageHistoryRoundedIcon />} iconPosition="start" label={"변경 검토 " + changes.length} />
        <Tab id="oss-order-tab-3" aria-controls="oss-order-panel-3" icon={<HistoryRoundedIcon />} iconPosition="start" label={"이력 " + (histories.length + actions.length)} />
      </Tabs>
      <section key={tab} role="tabpanel" id={"oss-order-panel-" + tab} aria-labelledby={"oss-order-tab-" + tab} tabIndex={0}
        className={"oss-order-tab-panel" + (tab === 2 ? " oss-order-change-panel" : "")}>
        {tab === 0 && (lines.length ? lines.map((line) => {
          const actionable = isActionNeeded(line) && !disabledByCancel;
          return <article className="oss-order-line" key={line.order_line_id}>
            <div className="oss-order-line-heading">
              <div><h3>{line.item_name_snapshot}</h3><p>{line.item_code_snapshot || "품목코드 미등록"} · {line.specification_snapshot || "규격 미등록"}</p></div>
              <div className="oss-order-line-actions">
                <OssStatusChip value={line.schedule_status} /><OssStatusChip type="risk" value={line.risk_level} />
                {actionable && <Button size="small" variant="outlined" startIcon={<FactCheckOutlinedIcon />} disabled={submitting} onClick={() => onAction(line)}>조치 처리</Button>}
              </div>
            </div>
            <dl className="oss-order-line-grid">
              <Info label="수량" value={Number(line.order_quantity || 0).toLocaleString() + " " + (line.unit_code || "")} />
              <Info label="요청 납기" value={line.requested_due_date} />
              <Info label="착수 필요일" value={line.required_start_date} />
              <Info label="확인 필요일" value={line.check_required_date} />
              <Info label="소요일 / 계획 여유일" value={(line.standard_lead_days ?? "-") + "일 / " + (line.planning_buffer_days ?? "-") + "일"} />
              <Info label="착수 기준 여유일" value={line.available_margin_days == null ? "-" : line.available_margin_days + "일"} />
            </dl>
            {actionable && <p className="oss-order-line-notice">{getLineActionReason(line, riskLevel, scheduleStatus)} 확인이 필요합니다.</p>}
          </article>;
        }) : <p className="oss-order-empty">등록된 품목이 없습니다.</p>)}
        {tab === 1 && <>
          <div className="oss-order-section-toolbar"><h3>주문 문서</h3><Button variant="outlined" startIcon={<AddRoundedIcon />} disabled={disabledByCancel || submitting} onClick={onDocumentUpload}>문서 업로드</Button></div>
          {documents.length ? documents.map((doc) => <article className="oss-action-document-entry" key={doc.document_id}>
            <DescriptionOutlinedIcon className="oss-action-file-icon" />
            <div><p className="oss-action-file-name">{doc.file_name}</p><p className="oss-action-file-meta">{documentTypes[doc.document_type] || "문서"} · v{doc.version_no || 1} · {doc.uploaded_at || "-"}</p></div>
            <Tooltip title="새 창에서 열기"><IconButton aria-label={doc.file_name + " 열기"} onClick={() => onDocumentOpen(doc)}><OpenInNewRoundedIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="문서 삭제"><IconButton aria-label={doc.file_name + " 삭제"} color="error" disabled={submitting} onClick={() => onDocumentDelete(doc)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton></Tooltip>
          </article>) : <p className="oss-order-empty">등록된 문서가 없습니다.</p>}
        </>}
        {tab === 2 && <>
          <div className="oss-order-section-toolbar"><h3>변경 검토</h3><Button variant="outlined" startIcon={<AddRoundedIcon />} disabled={disabledByCancel || submitting || !lines.length} onClick={onChangeRequest}>변경 요청</Button></div>
          {changes.length ? <div className="oss-order-change-grid"><OssChangeList rows={changes} loading={changesLoading} onReview={onReviewChange} height="100%" compact /></div> : <p className="oss-order-empty">등록된 변경 요청이 없습니다.</p>}
        </>}
        {tab === 3 && <>
          {!!actions.length && <><h3 className="oss-order-history-heading">조치 이력</h3>{actions.map((action) => <article className="oss-action-history-entry" key={action.action_id}>
            <div className="oss-action-history-meta"><strong>{action.action_title || displayLabel(actionType, action.action_type)}</strong><time>{action.due_at || "-"}</time></div>
            <p className="oss-action-history-status">{displayLabel(actionStatus, action.action_status)}</p>
            {action.result_note && <p>{action.result_note}</p>}
          </article>)}</>}
          {!!histories.length && <><h3 className="oss-order-history-heading">상태 이력</h3>{histories.map((history, index) => <article className="oss-action-history-entry" key={index}>
            <div className="oss-action-history-meta"><strong>{displayLabel(historyDomain, history.title)}</strong><time>{history.occurred_at}</time></div>
            <p className="oss-action-history-status">{statusDescription(history.description, history.title)}</p>
            {history.note && <p>{history.note}</p>}
          </article>)}</>}
          {!actions.length && !histories.length && <p className="oss-order-empty">등록된 이력이 없습니다.</p>}
        </>}
      </section>
    </>
  );
};
